import { useState, useRef, useCallback } from 'react';

// ─── useUndoableState ──────────────────────────────────────────────
// Bundles the four user-editable state arrays (placed, doors, drains,
// dimensions) and tracks a history of snapshots for undo/redo.
//
// Why bundled: a single user action (paste a group, apply a template)
// often touches multiple arrays at once. One snapshot per action keeps
// undo intuitive — Ctrl+Z reverses what the user perceives as one step.
//
// Two-tier API:
//  - setX(value | fn)     → updates state WITHOUT recording history.
//                           Use for in-progress changes (drag frames).
//  - commitHistory()      → snapshots current state into history.
//                           Call ONCE after a gesture ends, or after
//                           any discrete action (drop, delete, paste).
//  - undo() / redo()      → pop/push between history stacks.
//  - resetHistory(state)  → wipe history (used on project load/template).
//
// History cap: 50 entries. Beyond that, oldest is dropped.

const HISTORY_CAP = 50;

export function useUndoableState(initial) {
  const [placed, setPlacedRaw] = useState(initial.placed || []);
  const [doors, setDoorsRaw] = useState(initial.doors || []);
  const [drains, setDrainsRaw] = useState(initial.drains || []);
  const [dimensions, setDimensionsRaw] = useState(initial.dimensions || []);

  // Refs hold the live current values so commitHistory always snapshots
  // the latest, regardless of stale closures in event handlers.
  const placedRef = useRef(placed);
  const doorsRef = useRef(doors);
  const drainsRef = useRef(drains);
  const dimensionsRef = useRef(dimensions);

  // History stacks. past[] holds previous states; future[] holds states
  // we've undone past (so redo can pull from there).
  const pastRef = useRef([]);
  const futureRef = useRef([]);
  // The state as of the last commit (or load/undo/redo). Callers commit AFTER
  // an action, so past[] must store the state from BEFORE it — that's this ref.
  // Previously past[] stored the after-state, so the first Ctrl+Z restored the
  // screen you were already looking at (an off-by-one: "undo does nothing").
  const lastCommittedRef = useRef({
    placed: initial.placed || [],
    doors: initial.doors || [],
    drains: initial.drains || [],
    dimensions: initial.dimensions || [],
  });

  // Reactive booleans for "can undo" / "can redo" so UI can disable buttons.
  // Updated whenever stacks change.
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const syncFlags = () => {
    setCanUndo(pastRef.current.length > 0);
    setCanRedo(futureRef.current.length > 0);
  };

  // Wrapped setters: update both ref + state, but DON'T record history.
  // Keeps drag frames smooth without bloating undo stack.
  // Each setter is its own useCallback so React's hook rules are followed
  // (hooks must be called in the same order every render).
  const setPlaced = useCallback(value => {
    setPlacedRaw(prev => {
      const next = typeof value === 'function' ? value(prev) : value;
      placedRef.current = next;
      return next;
    });
  }, []);
  const setDoors = useCallback(value => {
    setDoorsRaw(prev => {
      const next = typeof value === 'function' ? value(prev) : value;
      doorsRef.current = next;
      return next;
    });
  }, []);
  const setDrains = useCallback(value => {
    setDrainsRaw(prev => {
      const next = typeof value === 'function' ? value(prev) : value;
      drainsRef.current = next;
      return next;
    });
  }, []);
  const setDimensions = useCallback(value => {
    setDimensionsRaw(prev => {
      const next = typeof value === 'function' ? value(prev) : value;
      dimensionsRef.current = next;
      return next;
    });
  }, []);

  // Snapshot current state and push onto history. Called by components
  // when a user action completes (mouse-up, paste, delete, etc.).
  const commitHistory = useCallback(() => {
    const snapshot = {
      placed: placedRef.current,
      doors: doorsRef.current,
      drains: drainsRef.current,
      dimensions: dimensionsRef.current,
    };
    // Nothing changed since the last commit — skip
    if (shallowEqualSnapshot(lastCommittedRef.current, snapshot)) return;
    // Push the BEFORE state; the current state becomes the new baseline
    pastRef.current = [...pastRef.current, lastCommittedRef.current].slice(-HISTORY_CAP);
    lastCommittedRef.current = snapshot;
    // New action invalidates redo stack
    futureRef.current = [];
    syncFlags();
  }, []);

  const applySnapshot = (snap) => {
    placedRef.current = snap.placed;
    doorsRef.current = snap.doors;
    drainsRef.current = snap.drains;
    dimensionsRef.current = snap.dimensions;
    setPlacedRaw(snap.placed);
    setDoorsRaw(snap.doors);
    setDrainsRaw(snap.drains);
    setDimensionsRaw(snap.dimensions);
  };

  const undo = useCallback(() => {
    if (pastRef.current.length === 0) return false;
    // Capture current as the "future" entry before reverting
    const current = {
      placed: placedRef.current,
      doors: doorsRef.current,
      drains: drainsRef.current,
      dimensions: dimensionsRef.current,
    };
    const prev = pastRef.current[pastRef.current.length - 1];
    pastRef.current = pastRef.current.slice(0, -1);
    futureRef.current = [...futureRef.current, current].slice(-HISTORY_CAP);
    applySnapshot(prev);
    lastCommittedRef.current = prev;
    syncFlags();
    return true;
  }, []);

  const redo = useCallback(() => {
    if (futureRef.current.length === 0) return false;
    const current = {
      placed: placedRef.current,
      doors: doorsRef.current,
      drains: drainsRef.current,
      dimensions: dimensionsRef.current,
    };
    const next = futureRef.current[futureRef.current.length - 1];
    futureRef.current = futureRef.current.slice(0, -1);
    pastRef.current = [...pastRef.current, current].slice(-HISTORY_CAP);
    applySnapshot(next);
    lastCommittedRef.current = next;
    syncFlags();
    return true;
  }, []);

  // Wipe history and set new state — used when loading project files
  // or applying templates, where pre-load undo doesn't make sense.
  const resetHistory = useCallback((state) => {
    const snap = {
      placed: state.placed || [],
      doors: state.doors || [],
      drains: state.drains || [],
      dimensions: state.dimensions || [],
    };
    applySnapshot(snap);
    lastCommittedRef.current = snap;   // loaded state is the new undo floor
    pastRef.current = [];
    futureRef.current = [];
    syncFlags();
  }, []);

  return {
    placed, doors, drains, dimensions,
    setPlaced, setDoors, setDrains, setDimensions,
    commitHistory, undo, redo, resetHistory,
    canUndo, canRedo,
  };
}

// Shallow array-identity check — if all four arrays are the same reference,
// no actual change happened, so we don't need a new history entry.
function shallowEqualSnapshot(a, b) {
  return a.placed === b.placed
    && a.doors === b.doors
    && a.drains === b.drains
    && a.dimensions === b.dimensions;
}
